/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 5 hook(s) need dependency review; fix each and remove this directive */
import { getBackendUrl } from "@Utils";
import { useTranslation, t, untranslated, type TranslationKey } from "@i18n";
import { Suspense, forwardRef, lazy, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { ComponentProps, MutableRefObject, Ref } from "react";
import { portalMeasurementUnitsUrl } from "@Services/dataManager";
import axiosWrapper from "@Services/axiosWrapper";
import type { GetMeasurementUnitsResponse } from "@ServerTypes/units/units.schema";
import type { UpdateBulkQuantityAndUnitOfFileResponse, DatabankViewData } from "@ServerTypes/queries/queries.schema";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import AddToTakeoffButton from "../AddToTakeoffButton";
import {
  addMeasurementsToTakeoffsFromNodes,
  getQueryData,
  prepareEstimationFieldNeedToRefresh,
} from "@Actions/estimation";
import { setEstimateFieldsNeedtoRefresh } from "@Reducers/EstimateList";
import Views from "./Views";
import type { ViewChangePayload } from "./Views";
import { RiBringToFront } from "react-icons/ri";
import { BiColorFill } from "react-icons/bi";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import ColorPickerComponent from "@CubeComponents/Mui_Form/ColorPickerComponent";
import { GrOverview } from "react-icons/gr";
import { AppDispatch, RootState } from "@Reducers/index";
import { useDispatch, useSelector } from "react-redux";
import { showToastNotification } from "@Reducers/toastSlice";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { getFileversionIdAndNodeOffsetIdMap } from "@Services/cubeViewHelper";
import FileColumnsTreeView from "@Components/FileColumnsTreeView";
import _ from "lodash";
import { ExcelExportService } from "@slickgrid-universal/excel-export";
import { resetDatabank, updateCurrentDatabankView } from "@Reducers/Databank";
import Divider from "@mui/material/Divider";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import IconComponent from "@CubeComponents/icons";
import { Popover } from "@mui/material";
import {
  convertToDotNotation,
  extractPropertyPartialName,
  getFilteredColumns,
  getModifiedNodesList,
} from "@Features/viewer-modals/components/Modal/SearchSet/searchSetHelper";
import Text from "@CubeComponents/html/Text/Text";
import { getIsViewerDependencyLoaded } from "@Services/utils/helpers";
import { dispatchModelLoadingToast } from "@Actions/Viewer";
import { HoopsViewerContext, CubeGlobal } from "@Features/viewer-core";
import SlickGrid1, { typedColumns, toolbarItem } from "@CubeComponents/html/SlickGrid1";
import type { ColumnDefinition, ValidatedColumnDefinition, ToolbarItem } from "@CubeComponents/html/SlickGrid1";
import { setModifiedModelProps, updateQuantityAndUnit } from "@Reducers/Viewer";
import { Selection } from "@ts3d-hoops/web-viewer";
import { applyFilter } from "@Features/viewer-modals/utils/searchOperators";
import { convertDate } from "@Services/luxonHelper";
import { getModelPropertiesData } from "@Services/utils/modelPropertiesDataDB";
import { useModelPropertiesWiped } from "@Services/useModelPropertiesWiped";
import ModelDataClearedState from "@Features/design-viewer/components/Modals/ModelDataClearedState";
import type { ModelPropertyNode, ModelPropertiesData, ModelPropertyValue } from "@WebTypes/modelProperties";
import type { SlickgridReactInstance, SlickGrid, SlickDataView, Column, GridOption } from "slickgrid-react";
import { objectIdString, type ObjectIdString } from "@Helpers/schemas/common.schema";

const ColumnChooser = lazy(() => import("./ColumnChooser"));

/**
 * A grid row: a {@link ModelPropertyNode} flattened to dotted-notation keys
 * (e.g. "properties.Quantity") by `convertToDotNotation`, plus a stable `id`.
 *
 * Identity fields derive from the files_properties zod single source of truth
 * (ModelPropertyNode ← `filePropertyDocument`), so they stay in lock-step with
 * the backend. The string index signature holds the dynamic flattened property
 * keys, which are vendor-defined and therefore genuinely `unknown`; the few reads
 * that need a shape assert it inline.
 */
type DatasetRow = {
  id: NonNullable<ModelPropertyNode["_id"]> | string;
  nodeId: NonNullable<ModelPropertyNode["nodeId"]>;
  fileVersionId: NonNullable<ModelPropertyNode["fileVersionId"]>;
  cubeUniqueId?: ModelPropertyNode["cubeUniqueId"];
  [dottedKey: string]: unknown;
};

/** A custom property whose value is an external link rather than a scalar. */
type LinkProperty = { URL?: string; displayText?: string };

/** Any non-null object can have a runtime-chosen key read off it; the value stays `unknown`. */
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

/**
 * `databankViewDataSchema` declares its own recursion as `z.lazy((): z.ZodType => …)`, so the stored
 * `views` map erases to `Record<string, unknown>`. Every writer (this file and Views.tsx) stores a
 * `DatabankViewData`, so the map is narrowed through this guard rather than an assertion.
 */
const isViewMap = (value: unknown): value is Record<string, DatabankViewData> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * The flatten helpers (`getModifiedNodesList` / `convertToDotNotation`) type their rows as open
 * records, because the dotted property keys are vendor-defined and only known at runtime. Every row
 * they build still carries the node's own `nodeId` / `fileVersionId` (copied verbatim off the node)
 * and an `id` synthesized from that pair — which is exactly {@link DatasetRow}'s identity contract,
 * the flatten just cannot state it. Narrowed once here instead of at each of the four call sites.
 */
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- see doc comment above: the identity fields are written by the flatten itself (SearchSet/searchSetHelper.ts `getModifiedNodesList`)
const asDatasetRows = (rows: Record<string, unknown>[]): DatasetRow[] => rows as DatasetRow[];

/** A grid column as slickgrid's raw API presents it, plus the flags our column definitions carry. */
type SlickColumn = Column<DatasetRow> & { disableMenuOptions?: boolean; groupable?: boolean };

/**
 * Round trip across slickgrid's own `Column` type: `getGridColumns()` builds this file's
 * {@link ColumnDefinition}s, which state `field` and the formatter value against the row type,
 * while the raw grid API only knows the vendor's `Column`. These are the same objects SlickGrid1
 * feeds to the grid; it keeps the identical helper internally (`asSlickColumns`) but does not
 * export it.
 */
function asSlickColumns(columns: ValidatedColumnDefinition<DatasetRow>[]): SlickColumn[] {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- vendor round trip, see doc comment above: the objects are unchanged, only the declared type differs
  return columns as unknown as SlickColumn[];
}

type MeasurementUnit = NonNullable<GetMeasurementUnitsResponse["result"]>[number];

// The app's strict, key-checked translation function, as the toolbar template components receive
// it. Keys still have to exist in en.json, so `t` is passed straight through with no assertion.
type TFunc = (key: TranslationKey) => string;
type QueryRule = Record<string, unknown> | null;
type FilterValue = { id: string } | null;
/**
 * SlickGrid1 instance + the `applyConditions` method the wrapper attaches at runtime, with
 * `slickGrid` restated over this grid's row type — `SlickgridReactInstance` declares it as
 * `SlickGrid<any>`, so every `getDataItem()` read off it would otherwise come back untyped.
 */
type GridInstance = Omit<SlickgridReactInstance, "slickGrid"> & {
  slickGrid: SlickGrid<DatasetRow>;
  applyConditions?: (data: {
    sortConditions: unknown[];
    filterConditions: unknown;
    groupConditions: unknown[];
  }) => void;
};

declare global {
  // Debug handles exposed on window for in-browser inspection of the grid/properties.

  var reactGridRef: MutableRefObject<GridInstance | null> | undefined;

  var modelPropertiesRef: MutableRefObject<ModelPropertyNode[]> | undefined;
}

type QuantificationTemplateProps = {
  columns: string[];
  t: TFunc;
  portalMeasurementsUnit: MutableRefObject<MeasurementUnit[]>;
  handleGlobalQuantityChange: (args: { field: string }) => void;
  handleGlobalUnitChange: (args: { value: string }) => void;
  handleGlobalSettingsUpdate: () => Promise<void>;
};

const QuantificationTemplate = ({
  columns,
  t,
  portalMeasurementsUnit,
  handleGlobalQuantityChange,
  handleGlobalUnitChange,
  handleGlobalSettingsUpdate,
}: QuantificationTemplateProps) => {
  const [anchorElQuantification, setAnchorElQuantification] = useState<HTMLElement | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const [isApplyEnabled, setIsApplyEnabled] = useState(false);
  const selectedQuantityRef = useRef("");
  const selectedUnitRef = useRef("");
  const urlSearchParams = new URLSearchParams(location.search);

  const onClose = () => {
    setAnchorElQuantification(null);
    setIsApplyEnabled(false);
    selectedQuantityRef.current = "";
    selectedUnitRef.current = "";
  };

  return (
    <>
      <ButtonComponent
        sx={{ flexShrink: 0 }}
        onClick={(e: React.MouseEvent<HTMLElement>) => setAnchorElQuantification(e.currentTarget)}
        buttonType={BUTTON_TYPES.TEXT_BUTTON}
        startIcon={
          <div className="flex items-center justify-center w-6 h-6">
            <IconComponent name={"Quantification"} width="1.5rem" height={"1.5rem"} />
          </div>
        }
        endIcon={<ArrowDropDownIcon />}
        // @ts-expect-error -- a `&&`/`||` guard yields `string | null` where the prop wants `boolean | undefined`. No erased fix: the correct form is `!!(...)`, which is emitted code.
        disabled={urlSearchParams.get("versionId")}
      >
        <Text>{t("Quantification")}</Text>
      </ButtonComponent>
      <Popover
        id={"popoverId"}
        open={Boolean(anchorElQuantification)}
        anchorEl={anchorElQuantification}
        onClose={onClose}
        sx={{
          "& .MuiPaper-root": {
            boxShadow: "0px 0.25rem 0.75rem rgba(0, 0, 0, 0.1)",
            borderRadius: "0.5rem",
            padding: "1.25rem",
            width: "26%",
            marginTop: "0.625rem",
            marginInlineStart: "0.625rem",
            zIndex: 9999999,
          },
        }}
      >
        <div className="w-full h-full flex flex-col">
          <span className="text-[#192048] text-base font-bold leading-normal mb-6">
            <Text>{t("Quantification")}</Text>
          </span>
          <span className="text-[color:var(--Text-text-primary,#192048)] text-sm font-semibold leading-[120%] mb-3">
            <Text>{t("Quantity Parameter")}</Text>
          </span>
          <span className="text-[color:var(--Text-text-secondary,#666A84)] font-source-sans-pro text-xs font-normal leading-[120%] mb-3">
            <Text sx={{ whiteSpace: "normal" }}>
              {t(
                "Specify the parameter using which the Quantity of the elements will be fetched. e.g. Volume, Area, Length etc.",
              )}
            </Text>
          </span>
          <FileColumnsTreeView
            change={(args: { field: string }) => {
              selectedQuantityRef.current = args.field;
              setIsApplyEnabled(!!(args.field || selectedUnitRef.current));
              handleGlobalQuantityChange(args);
            }}
            columns={columns}
          />
          <span className="text-[color:var(--Text-text-primary,#192048)] text-sm font-semibold leading-[120%] mb-3 mt-4">
            <Text>{t("Unit")}</Text>
          </span>
          <span className="text-[color:var(--Text-text-secondary,#666A84)] font-source-sans-pro text-xs font-normal leading-[120%] mb-3">
            <Text sx={{ whiteSpace: "normal" }}>
              {t(
                "The unit of the quantity fetched from above parameter. Please select the appropriate unit of measurement by referring to the properties in the metadata. Using the correct unit ensures accurate values and enables proper unit conversion within the takeoff.",
              )}
            </Text>
          </span>
          <AutoComplete
            options={portalMeasurementsUnit.current}
            valueField="_id"
            getOptionLabel={(option) => option.nameWithSymbol || ""}
            onChange={(_event, newValue) => {
              // AutoComplete yields the selected MeasurementUnit; the ref/handler want its _id string.
              const value = newValue?._id || "";
              selectedUnitRef.current = value;
              setIsApplyEnabled(!!(value || selectedQuantityRef.current));
              handleGlobalUnitChange({ value });
            }}
            renderInput={(params) => <TextFieldComponent {...params} placeholder={t("Unit")} />}
          />
          <div className="flex p-4 justify-center items-center gap-4 self-stretch rounded-lg bg-[rgba(255,207,85,0.25)] my-4">
            <span className="text-[color:var(--Text-text-primary,#192048)] font-source-sans-pro text-sm font-normal leading-[120%]">
              <Text sx={{ whiteSpace: "normal" }}>
                {/* TODO(i18n): this sentence has no key in public/locales/en.json, so `t()` has
                    always fallen back to rendering it verbatim in every language. Add the key
                    there and switch this back to `t(...)`. */}
                {untranslated(
                  "This will override any pre-existing data in the Quantity and Unit columns. Takeoffs will remain unaffected.",
                )}
              </Text>
            </span>
          </div>
          <div className="w-[30%]">
            <ButtonComponent
              buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
              onClick={async () => {
                setIsApplying(true);
                await handleGlobalSettingsUpdate();
                setIsApplying(false);
                onClose();
              }}
              disabled={!isApplyEnabled || isApplying}
              loading={isApplying}
            >
              <Text>{t("Apply")}</Text>
            </ButtonComponent>
          </div>
        </div>
      </Popover>
    </>
  );
};

/** The remote-data query descriptor SelectDropDown forwards to its data source. */
type RemoteQuery = ComponentProps<typeof SelectDropDown<QueryFilterOption>>["query"];

type FilterDropdownProps = {
  remoteDataSource: string;
  query: RemoteQuery;
  value: FilterValue | undefined;
  onChange: (args: { id: string; query: string } | null) => void;
  placeholder: string;
};
/** Saved-query option served by the queriesData remote source (matched on `id`). */
type QueryFilterOption = { id: string; query?: string; [key: string]: unknown };

const FilterDropdownComp = ({ remoteDataSource, query, value, onChange, placeholder }: FilterDropdownProps) => (
  <SelectDropDown<QueryFilterOption>
    remoteDataSource={remoteDataSource}
    query={query}
    value={value}
    // The dropdown declares `onChange` over its whole option union (single option, multi-select
    // array, or cleared). This filter is single-select, so the array arm never arrives; the two
    // fields the handler reads are picked off the selected saved query here rather than asserting
    // the callback into a narrower shape.
    onChange={(selected) => {
      const option = Array.isArray(selected) ? selected[0] : selected;
      onChange(option ? { id: option.id, query: option.query ?? "" } : null);
    }}
    buttonType="textButton"
    clearable
    popperWidth={200}
    placeholder={placeholder}
    fields={{
      text: "name",
      value: "id",
    }}
    {...({} as ComponentProps<typeof SelectDropDown<QueryFilterOption>>)}
  />
);

type ViewsDropdownProps = {
  id: string;
  fileVersionId: string;
  onChange: (data: ViewChangePayload) => void;
};
const ViewsDropdownComp = ({ id, fileVersionId, onChange }: ViewsDropdownProps) => (
  <div className="flex-shrink-0 ms-2 me-4">
    {" "}
    <Views id={id} fileVersionId={fileVersionId} onChange={onChange} />{" "}
  </div>
);

type AddToTakeoffWrapProps = {
  onSuccess: (takeoffsChecked: ObjectIdString[]) => void | Promise<void>;
  estimateId: ObjectIdString | number;
  buttonRef: Ref<HTMLButtonElement>;
  onCreated: () => void;
};
const AddToTakeoffWrap = ({ onSuccess, estimateId, buttonRef, onCreated }: AddToTakeoffWrapProps) => (
  <div className="relative">
    {" "}
    <AddToTakeoffButton onSuccess={onSuccess} estimateId={estimateId} ref={buttonRef} onCreated={onCreated} />{" "}
  </div>
);

type ActionButtonsProps = {
  onSelectAll: () => void;
  onIsolateAll: () => void;
  onSetColorAll: () => void;
  colorValue: string;
  onColorChange: (color: { value: string }) => void;
};
const ActionButtons = ({ onSelectAll, onIsolateAll, onSetColorAll, colorValue, onColorChange }: ActionButtonsProps) => (
  <div className="relative z-0 flex items-center gap-4 px-4">
    {" "}
    <ToolTipComponent showTipPointer position="bottom" content={t("Select")}>
      {" "}
      <GrOverview size={18} onClick={onSelectAll} className="cursor-pointer" />
    </ToolTipComponent>{" "}
    <ToolTipComponent showTipPointer position="bottom" content={t("Isolate")}>
      {" "}
      <RiBringToFront size={18} onClick={() => onIsolateAll()} className="cursor-pointer" />{" "}
    </ToolTipComponent>{" "}
    <ToolTipComponent showTipPointer position="bottom" content={t("Color")}>
      {" "}
      <BiColorFill size={18} onClick={onSetColorAll} className="cursor-pointer" />{" "}
    </ToolTipComponent>{" "}
    <ToolTipComponent showTipPointer position="bottom" content={t("Modify Color")}>
      {" "}
      <ColorPickerComponent value={colorValue} change={onColorChange} enableOpacity={false} />{" "}
    </ToolTipComponent>{" "}
  </div>
);

type ColumnChooserBtnProps = { onClick: () => void };
const ColumnChooserBtn = ({ onClick }: ColumnChooserBtnProps) => (
  <ButtonComponent iconButton sx={{ flexShrink: 0 }} buttonType={BUTTON_TYPES.TEXT_BUTTON} onClick={onClick}>
    <IconComponent width={"1.5rem"} height={"1.5rem"} name="Column_Chooser" />
  </ButtonComponent>
);

const SeparatorLine = () => (
  <Divider orientation="vertical" variant="middle" sx={{ height: "2rem", background: "#D4E8FA" }} flexItem />
);

const createSeparatorItem = (): ToolbarItem => ({ id: "separator", template: SeparatorLine });

const ViewsDropdownItem = ({
  id,
  fileVersionId,
  onViewChange,
}: {
  id: string;
  fileVersionId: string;
  onViewChange: (data: ViewChangePayload) => void;
}) => <ViewsDropdownComp id={id} fileVersionId={fileVersionId} onChange={onViewChange} />;

const AddToTakeoffItem = ({
  handleAddMeasurementClick,
  estimateId,
  addToTakeoffButtonRef,
  onTakeoffButtonCreated,
}: {
  handleAddMeasurementClick: (takeoffsChecked: ObjectIdString[]) => void | Promise<void>;
  estimateId: ObjectIdString | number;
  addToTakeoffButtonRef: Ref<HTMLButtonElement>;
  onTakeoffButtonCreated: () => void;
}) => (
  <AddToTakeoffWrap
    onSuccess={handleAddMeasurementClick}
    estimateId={estimateId}
    buttonRef={addToTakeoffButtonRef}
    onCreated={onTakeoffButtonCreated}
  />
);

const QueryFilterItem = ({
  query,
  filterRef,
  handleQueryFilterChange,
  t,
}: {
  query: RemoteQuery;
  filterRef: MutableRefObject<{ value: FilterValue } | null>;
  handleQueryFilterChange: (args: { id: string; query: string } | null) => void;
  t: TFunc;
}) => (
  <FilterDropdownComp
    remoteDataSource={`${process.env.REACT_APP_CUBE_BACKEND}/queries/queriesData`}
    query={query}
    value={filterRef.current?.value}
    onChange={handleQueryFilterChange}
    placeholder={t("Filter by query")}
  />
);

const QuantificationItem = ({
  columns,
  t,
  portalMeasurementsUnit,
  handleGlobalQuantityChange,
  handleGlobalUnitChange,
  handleGlobalSettingsUpdate,
}: QuantificationTemplateProps) => (
  <QuantificationTemplate
    columns={columns}
    t={t}
    portalMeasurementsUnit={portalMeasurementsUnit}
    handleGlobalQuantityChange={handleGlobalQuantityChange}
    handleGlobalUnitChange={handleGlobalUnitChange}
    handleGlobalSettingsUpdate={handleGlobalSettingsUpdate}
  />
);

interface SearchSetProps {
  /**
   * Parent estimate id. Reaches this component through the viewer's untyped (`any`) prop chain
   * from the `estimateId` URL query param; a legacy numeric form is still coerced with `String()`
   * before it is sent, hence the union.
   */
  estimateId?: ObjectIdString | number;
  fileVersionId: ObjectIdString;
  allowGrouping?: boolean;
  disableAddMeasurementToTakeoff?: boolean;
  hideViews?: boolean;
  hideQueryFiltering?: boolean;
  hideExcludedClumns?: boolean;
  isFromQuery?: boolean;
  triggerIsolate?: boolean;
  setTriggerIsolate?: (value: boolean) => void;
  onSaveColumns?: (columns: string[]) => void;
  filterByQueryRule?: { query: QueryRule; columns?: string[] };
  filterQueryId?: string;
  enableAdditionalDataSelection?: boolean;
  id?: string;
}

const SearchSet = forwardRef<unknown, SearchSetProps>(
  ({
    estimateId,
    fileVersionId,
    allowGrouping = true,
    disableAddMeasurementToTakeoff = false,
    hideViews = false,
    hideQueryFiltering = false,
    hideExcludedClumns = false,
    isFromQuery = false,
    triggerIsolate = false,
    setTriggerIsolate,
    onSaveColumns,
    filterByQueryRule,
    id = "standaloneDatabank",
  }) => {
    const { t } = useTranslation();
    const [showShimmer, setShowShimmer] = useState(true);
    const filterRef = useRef<{ value: { id: string } | null } | null>(null);
    const [gridOptions, setGridOptions] = useState<Partial<GridOption> | undefined>(undefined);
    const [columnDefinitions, setColumnDefinitions] = useState<ValidatedColumnDefinition<DatasetRow>[] | null>(null);
    const [gridReady, setGridReady] = useState(false);
    const estimateFieldsNeedToRefresh = useSelector((state: RootState) => state.estimateFieldsNeedToRefresh);
    const HoopsModalStatus = useSelector((state: RootState) => state.HoopsModalStatus);
    const modelProperties = useSelector((state: RootState) => state.modelProperties);
    const estimateError = useSelector((state: RootState) => state.estimateError);
    const modifiedModelProps = useSelector((state: RootState) => state.modifiedModelProps);
    const isModelDataWiped = useModelPropertiesWiped(fileVersionId);
    // Merged model-properties (keyed [fileVersionId][nodeId]) held in a single
    // ref — no per-component useState copy. Cleared before each refetch so the
    // old snapshot is released before the next is built (caps peak heap). The
    // flattened list the query-filter needs is derived on demand from this ref.
    const modelDataRef = useRef<ModelPropertiesData>({});
    // Records the exact dataVersion produced by the databank's own quantity+unit
    // update (which patches the grid + ref locally). The version effect skips the
    // refetch for ONLY that version — a concurrent foreign bump has a different
    // version and still refetches. -1 = nothing to skip.
    const skipRefetchForVersionRef = useRef<number>(-1);
    // Latest committed modelProperties slice mirrored into a ref, so callbacks
    // read the current dataVersion off React state (no imperative store read).
    const modelPropertiesStateRef = useRef(modelProperties);
    modelPropertiesStateRef.current = modelProperties;
    const reactGridRef = useRef<GridInstance | null>(null);
    const dataviewObjRef = useRef<SlickDataView | null>(null);
    const gridObjRef = useRef<SlickGrid | null>(null);
    const _darkModeRef = useRef(false);
    const portalMeasurementsUnit = useRef<MeasurementUnit[]>([]);
    globalThis.reactGridRef = reactGridRef;
    const { isModelStructureReady, isSettingsApplied, viewerImpl } = useContext(HoopsViewerContext);
    const [dataset, setDataset] = useState<DatasetRow[]>([]);

    const reactGridReady = (reactGrid: SlickgridReactInstance) => {
      setGridReady(true);
      reactGridRef.current = reactGrid;
      gridObjRef.current = reactGrid.slickGrid;
      dataviewObjRef.current = reactGrid.dataView;
    };

    /** The grid rows currently selected (empty if the grid isn't mounted yet). */
    const getSelectedDataItems = (): DatasetRow[] => {
      const grid = reactGridRef.current?.slickGrid;
      if (!grid) return [];
      return grid.getSelectedRows().map((rowIdx: number) => grid.getDataItem(rowIdx));
    };

    const refreshDatasetFromModelData = (nextData: ModelPropertiesData) => {
      const refreshedById = new Map<DatasetRow["id"], DatasetRow>(
        asDatasetRows(getModifiedNodesList(nextData)).map((row) => [row.id, row]),
      );
      setDataset((prev) => prev.map((row) => refreshedById.get(row.id) ?? row));
    };

    useEffect(
      () => () => {
        const panel = document.querySelector<HTMLElement>(".panel-wm-content");
        panel?.classList.remove("dark-mode");
        const host = document.querySelector<HTMLElement>(`#${id}`);
        if (host) {
          host.dataset.bsTheme = "light";
        }
      },
      [id],
    );

    const getGridColumns = (extraColumns: string[] = []): ValidatedColumnDefinition<DatasetRow>[] => {
      // keep CUBE ID / CUBE Name pinned as fixed identity columns, but allow
      // Quantity / Unit (excluded constants) to be drag-reordered with the rest
      // of the dynamic columns so users can arrange the grid as they like
      let columns: ColumnDefinition<DatasetRow, string>[] = compulsoryColumnsConstant.map((column) => ({
        ...column,
        reorderable: false,
        groupable: false,
      }));
      if (!hideExcludedClumns) {
        columns = [
          ...columns,
          ...excludedColumnsConstant.map((column) => ({
            ...column,
            groupable: false,
          })),
        ];
      }
      columns = [
        ...columns,
        ...extraColumns.map((column): ColumnDefinition<DatasetRow, string> => {
          let columnName = extractPropertyPartialName(column);
          let label: string = column;
          let isFieldDatetime: boolean = false;

          if (column.startsWith("customProperties.")) {
            const columnData = modelProperties.questionIdAndNameMapper?.[columnName];
            if (columnData) {
              columnName = columnData.question;
              isFieldDatetime = columnData.type === "date-time";
            }
            label = `customProperties.${columnName}`;
          }
          return {
            minWidth: 150,
            field: column,
            id: column.replaceAll(".", ""),
            label,
            name: columnName,
            groupable: true,
            // dynamic columns must carry these flags on initial render so the
            // header menu shows filter/sort options and drag-to-reorder works
            // before any view is applied
            filterType: isFieldDatetime ? ("Date" as const) : ("Text" as const),
            filterable: true,
            sortable: true,
            reorderable: true,
            formatter: (_row, _cell, _value, _columnDef, dataContext) => {
              const fieldValue = _.get(dataContext, column);
              if (column.startsWith("customProperties.") && fieldValue) {
                if (isFieldDatetime) {
                  return convertDate(Number(fieldValue), "dateForTooltip");
                }
                if (Array.isArray(fieldValue)) {
                  return fieldValue.join(", ");
                }
                if (typeof fieldValue == "object") {
                  return `<a
                            href="${(fieldValue as LinkProperty).URL}"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gradient_color cursor-pointer underline underline-offset-2"
                          >
                            ${(fieldValue as LinkProperty).displayText}
                          </a>`;
                }
              }
              return fieldValue;
            },
            excelExportOptions: {
              valueParserCallback: (_value, { dataContext }) => {
                const fieldValue: unknown = _.get(dataContext, column);
                // A link property is stored as an object ({@link LinkProperty}); scalars export as-is.
                if (isRecord(fieldValue)) {
                  return fieldValue["URL"];
                }
                return fieldValue || "";
              },
            },
          };
        }),
      ];
      return typedColumns<DatasetRow>(columns);
    };

    const excludedColumnsConstant: ColumnDefinition<DatasetRow, string>[] = [
      {
        id: "propertiesQuantity",
        field: "properties.Quantity",
        isFrozen: false,
        minWidth: 150,
        // `disableMenuOptions: true` suppressed the header menu and filter UI,
        // and `allowReordering: false` blocked drag-to-reorder
        filterType: "Number" as const,
        filterable: true,
        sortable: true,
        reorderable: true,
        label: extractPropertyPartialName("properties.Quantity"),
        name: extractPropertyPartialName("properties.Quantity"),
        excelExportOptions: {
          valueParserCallback: (_value, { dataContext }) => _.get(dataContext, "properties.Quantity") || "",
        },
      },
      {
        id: "propertiesUnit",
        field: "properties.Unit",
        isFrozen: false,
        minWidth: 150,
        // filter is text-based: _valueGetter below resolves the stored unit id
        // to its display label, which is what the user sees and filters by
        filterType: "Text" as const,
        filterable: true,
        sortable: true,
        reorderable: true,
        label: extractPropertyPartialName("properties.Unit"),

        name: extractPropertyPartialName("properties.Unit"),
        formatter: (_row, _cell, value) => {
          const unitData = portalMeasurementsUnit.current.find((data) => data._id == value);
          if (unitData) {
            return unitData.nameWithSymbol;
          }
          // legacy rows store the unit name itself instead of a unit id
          return value ?? "";
        },
        // resolves unit ids to display names so the filter options dropdown and
        // filter matching work on what the user sees, not raw unit ids; legacy
        // rows store plain unit names ("Count") — pass those through unchanged
        _valueGetter: (row) => {
          const rawUnit = _.get(row, "properties.Unit");
          const unitData = portalMeasurementsUnit.current.find((data) => data._id == rawUnit);
          return unitData ? unitData.nameWithSymbol : (rawUnit ?? "");
        },
        excelExportOptions: {
          valueParserCallback: (_value, { dataContext }) => {
            const rawUnit = _.get(dataContext, "properties.Unit");
            const unitData = portalMeasurementsUnit.current.find((data) => data._id == rawUnit);
            if (unitData) {
              return unitData.nameWithSymbol;
            }
            // legacy rows store the unit name itself instead of a unit id
            return rawUnit ?? "";
          },
        },
      },
    ];

    const compulsoryColumnsConstant: ColumnDefinition<DatasetRow, string>[] = [
      {
        field: "nodeId",
        id: "nodeId",
        name: t("CUBE ID"),
        disableMenuOptions: true,
        renderHeader: () => (
          <span className="font-semibold">
            <Text>{t("CUBE ID")}</Text>
          </span>
        ),
        width: 150,
        minWidth: 110,
        resizable: true,
        label: t("CUBE ID"),

        allowReordering: false,
        allowGrouping: false,
      },
      {
        id: "propertiesCUBE Name",
        field: "properties.CUBE Name",
        name: extractPropertyPartialName("properties.CUBE Name"),
        width: 200,
        minWidth: 150,
        resizable: true,
        label: extractPropertyPartialName("properties.CUBE Name"),
        disableMenuOptions: true,
        allowReordering: false,
        allowGrouping: false,
        excelExportOptions: {
          valueParserCallback: (_value, { dataContext }) => _.get(dataContext, "properties.CUBE Name") || "",
        },
      },
    ];

    const options: Partial<GridOption> = {
      autoResize: {
        container: `#${id}`,
        applyResizeToContainer: true,
        calculateAvailableSizeBy: "container",
      },
      showCustomFooter: true,
      enableHeaderMenu: false,
      enableSorting: true,
      enableColumnReorder: true,
      multiColumnSort: true,
      enableCheckboxSelector: true,
      // no enableAutoSizeColumns override: let SlickGrid1 size dynamically —
      // columns stretch to fill the panel when they fit (so the last column
      // ends at the grid edge like every other grid) and fall back to
      // horizontal scrolling when they overflow
      checkboxSelector: {
        toolTip: "",
      },
      draggableGrouping: {
        dropPlaceHolderText: "Drag a column header here to group its column",
        deleteIconCssClass: "mdi mdi-close color-danger",
        sortAscIconCssClass: "mdi mdi-arrow-up",
        sortDescIconCssClass: "mdi mdi-arrow-down",
      },
      darkMode: _darkModeRef.current,
      externalResources: [new ExcelExportService()],
      enableExcelExport: true,
    };

    useEffect(() => {
      // `modifiedModelProps` only ever carries the "customMetadata" variant (its `type` is that
      // single literal in ModifiedModelPropsState); a second variant would have to widen the
      // destructuring below, so there is nothing to discriminate on here.
      if (!modifiedModelProps) return;

      const { fileVersionId, nodeId, fieldsData } = modifiedModelProps;

      const nodeIds = Array.isArray(nodeId) ? nodeId : [nodeId];

      // clone current grid-backed data
      const nextData: ModelPropertiesData = { ...modelDataRef.current };

      nodeIds.forEach((id: number) => {
        const currNode = nextData[fileVersionId]?.[id];
        if (!currNode) return;

        const updatedCustomProperties = { ...(currNode.customProperties || {}) };

        if (Array.isArray(fieldsData.value) && fieldsData.value.length === 0) {
          delete updatedCustomProperties[fieldsData._id];
        } else {
          updatedCustomProperties[fieldsData._id] = fieldsData.value;
        }

        nextData[fileVersionId] = {
          ...nextData[fileVersionId],
          [id]: {
            ...currNode,
            customProperties: updatedCustomProperties,
            // KNOWN BUG, preserved verbatim: `selectedFormTemplate` is optional on the payload, so
            // this can store `{ _id: undefined }` on the node. Erased assertion; a guard would change
            // behaviour.
            selectedFormTemplate: { _id: fieldsData.selectedFormTemplate! },
          },
        };
      });

      refreshDatasetFromModelData(nextData);

      dispatch(setModifiedModelProps(null));
    }, [modifiedModelProps]);

    // Fetch the merged model-properties from IndexedDB whenever the version
    // the data lives in a single ref (no useState copy) and we drive the derived
    // grid state by hand, since ref writes don't trigger renders.
    useEffect(() => {
      if (!fileVersionId || !modelProperties.isLoaded) return;

      // The databank patches its own quantity+unit edit into the grid + ref
      // locally; skip the IDB re-read + full rebuild for ONLY the exact version
      // that edit produced (a concurrent foreign bump differs and still
      // refetches). Clear the loader too, in case a superseded in-flight fetch
      // left it on.
      if (skipRefetchForVersionRef.current === modelProperties.dataVersion) {
        skipRefetchForVersionRef.current = -1;
        setShowShimmer(false);
        return;
      }

      // Release the previous (possibly very large) snapshot BEFORE building the
      // next one, so peak heap stays ~1x; the loader covers the gap.
      setShowShimmer(true);
      modelDataRef.current = {};

      // Latest-wins guard: bumps across ticks start overlapping async reads that
      // can resolve out of order; only the most recent one may commit.
      let active = true;
      // getModelPropertiesData expands the merged file to its constituent
      // versions and resolves units/portalUnits/engineType internally from
      // `originalFileVersionId` — no store read or id resolution needed here.
      getModelPropertiesData({ originalFileVersionId: fileVersionId }).then((result) => {
        if (!active) return;
        // `getModelPropertiesData` declares its nodes as `MergedNode` (`nodeId?: number | string`),
        // while every consumer here reads the same documents at their wire type, ModelPropertyNode.
        // The real fix is typing that function's return as ModelPropertiesData — same documents,
        // one file up (@Services/utils/modelPropertiesDataDB).
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- IDB merge layer types its nodes loosely, see comment above; nothing is transformed here
        modelDataRef.current = result as ModelPropertiesData;

        // Flatten every node to dot-notation rows ONCE, then reuse them for both
        // column detection and the grid dataset — avoids a second full
        // convertToDotNotation pass over the whole tree on each dataVersion bump.
        const rows = asDatasetRows(getModifiedNodesList(result));

        const columnSet = new Set<string>();
        for (const row of rows) {
          for (const key of Object.keys(row)) {
            if (/^(properties|customProperties|typeProperties)\./.test(key)) {
              columnSet.add(key);
            }
          }
        }
        const filteredColumns = getFilteredColumns(Array.from(columnSet)) as string[];
        const gridColumns = getGridColumns(filteredColumns);

        setColumnDefinitions(gridColumns);
        initialiseToolbar(filteredColumns);
        setGridOptions(options);
        // Refetch: grid already mounted -> commit rows now. First load: gridReady
        // is still false; the effect below commits them once the grid mounts.
        if (modelProperties.isFileFormTemplatesLoaded && gridReady) {
          setDataset(rows);
          setShowShimmer(false);
        }
      });

      return () => {
        active = false;
      };
    }, [modelProperties.dataVersion, fileVersionId]);

    // Commit the built rows once the grid is mounted and form templates are
    // ready (first-load ordering: columns set -> grid mounts -> gridReady flips).
    useEffect(() => {
      if (modelProperties.isLoaded && modelProperties.isFileFormTemplatesLoaded && gridReady) {
        setShowShimmer(true);
        setDataset(asDatasetRows(getModifiedNodesList(modelDataRef.current)));
        setShowShimmer(false);
      }
    }, [gridReady, modelProperties.isFileFormTemplatesLoaded]);

    const dispatch = useDispatch<AppDispatch>();

    const addToTakeoffButtonRef = useRef<HTMLButtonElement | null>(null);

    const [disableTakeoffButton, setDisableTakeoffButton] = useState(true);
    const [openColumnChooserModal, setOpenColumnChooserModal] = useState(false);
    const globalQuantityValue = useRef("");
    const globalUnitValue = useRef("");
    const [toolbar, setToolbar] = useState<ToolbarItem[]>([]);

    const isAllRowsSelectedRef = useRef(false);
    const nodeColor = useRef("#0000FF");

    const [query] = useState({
      params: {
        fileVersionId,
      },
      fileVersionId,
    });

    const {
      viewsLoaded,
      views = {},
      selectedView,
      // `?? {}` outside the selector — inside it allocated a new object per call and re-rendered on
      // every dispatched action.
    } = useSelector((state: RootState) => state.Databank[id]?.[fileVersionId]) ?? {};

    const viewsRef = useRef<Record<string, DatabankViewData>>({});
    const selectedViewRef = useRef<string>("");
    selectedViewRef.current = selectedView ?? "";
    // `databankViewDataSchema` declares its own recursion as `z.lazy((): z.ZodType => …)`, so the
    // stored `views` map erases to Record<string, unknown>. Every writer (this file and Views.tsx)
    // stores DatabankViewData, so the map is narrowed through a guard rather than an assertion —
    // the same guard Views.tsx uses.
    viewsRef.current = isViewMap(views) ? views : {};

    const onTakeoffButtonCreated = () => {
      if (addToTakeoffButtonRef.current) addToTakeoffButtonRef.current.disabled = true;
    };

    useEffect(() => {
      if (addToTakeoffButtonRef.current) {
        addToTakeoffButtonRef.current.disabled = !!disableTakeoffButton;
      }
    }, [disableTakeoffButton]);

    const getPortalMeasurementUnits = useCallback(async () => {
      const response = await axiosWrapper.post<GetMeasurementUnitsResponse>(portalMeasurementUnitsUrl, {});
      portalMeasurementsUnit.current = response.data.result ?? [];
    }, []);

    const fetchData = useCallback(async () => {
      await getPortalMeasurementUnits();
    }, [getPortalMeasurementUnits]);

    useEffect(() => {
      fetchData();
    }, [fetchData]);

    useEffect(() => {
      if (!(filterByQueryRule != undefined && gridReady && triggerIsolate)) return;

      if (!getIsViewerDependencyLoaded({ viewerImpl, isModelStructureReady, isSettingsApplied, hwv: CubeGlobal.hwv })) {
        dispatch(dispatchModelLoadingToast());
        return;
      }

      (async () => {
        const records = await updateAllParameterOfGrid(filterByQueryRule.query, filterByQueryRule.columns);
        await handleIsolateOnInit(records);
        setTriggerIsolate?.(false);
      })();
    }, [filterByQueryRule, gridReady, triggerIsolate]);

    const [enableWrapText, setEnableWrapText] = useState(false);

    const updateAllParameterOfGrid = async (query: QueryRule, columns: string[] = [], groupColumns: string[] = []) => {
      if (reactGridRef.current) {
        // Flattened complete node list for the filter, derived on demand from
        // the single keyed ref (no separate persistent flattened ref).
        const allNodes = Object.values(modelDataRef.current).flatMap((obj) => Object.values(obj));
        const { filteredData, allColumns } = applyFilter(allNodes, query);
        // `_id` is no longer synthesized by the IDB merge layer. Derive a
        // stable grid row id from `(fileVersionId, nodeId)` which is unique
        // within and across files.
        const convertedData = asDatasetRows(
          filteredData.map((data, index) => ({
            // `nodeId` is required on every node (files_properties declares `nodeId: z.number()`),
            // but `fileVersionId` is not — nodes missing it would all share one id and break the
            // DataView, so those fall back to their position.
            id: data.fileVersionId != null ? `${data.fileVersionId}_${data.nodeId}` : `node_${index}`,
            ...convertToDotNotation(data),
          })),
        );
        setDataset(convertedData);
        for (const column of columns) allColumns.add(column);
        for (const column of groupColumns) allColumns.add(column);
        // CUBE-25959: the grid may not be mounted yet when initializeView races with grid setup —
        // that is what the enclosing `if (reactGridRef.current)` covers; the instance carries its
        // `slickGrid` from the moment it is created (reactGridReady).
        const gridInstance = reactGridRef.current;
        const grid = gridInstance.slickGrid;
        const buttoncssClass = "mdi mdi-dots-vertical text-slate-900 inline-block  cursor-pointer mt-0";
        // The first column is the grid's own checkbox-selector column, taken back off the grid
        // unchanged; the rest are rebuilt from the current column set.
        const [selectorColumn] = grid.getColumns();
        const sourceColumns: SlickColumn[] = [
          ...(selectorColumn ? [selectorColumn] : []),
          ...asSlickColumns(getGridColumns(Array.from(allColumns))),
        ];
        grid.setColumns(
          sourceColumns.map((column) => {
            if (column.disableMenuOptions) return column;

            let updatedColumn: SlickColumn = { ...column };
            updatedColumn.headerCssClass = (updatedColumn.headerCssClass || "") + " custom-header-column";

            const headerConfig = {
              buttons: [
                {
                  cssClass: buttoncssClass,
                  command: "toggle-highlight",
                  // The header button's tooltip is the column id; slickgrid puts it straight into
                  // the DOM `title`, where a numeric id was being stringified anyway.
                  tooltip: String(column.id),
                },
              ],
            };
            updatedColumn = {
              ...updatedColumn,
              header: headerConfig,
              sortable: true,
              groupable: true,
              filterable: true,
              type: "string",
            };

            return updatedColumn;
          }),
        );
        gridInstance.resizerService.resizeColumnsByCellContent(true);
        requestAnimationFrame(() => {
          const grid = reactGridRef.current?.slickGrid;
          if (!grid) return; // CUBE-25959
          grid.resizeCanvas();
          grid.setOptions({ enableAutoSizeColumns: true });
          grid.autosizeColumns();
        });
        return convertedData;
      }
    };

    const getQueryResponse = async (id: string) => {
      const response = await getQueryData({ fileVersionId, id });
      const result = response.data.result;
      const first = Array.isArray(result) ? result[0] : undefined;
      if (response.data.status && first?.query) {
        return JSON.parse(first.query);
      }
      return null;
    };

    const onViewChange = (data: ViewChangePayload) => {
      initializeView(
        data.query,
        data.columns,
        data.group,
        data.sortConditions || [],
        data.filterConditions || {},
        data.groupConditions || [],
      );
    };

    const initializeView = async (
      query: string,
      columns: string[],
      group: string[],
      sortConditions: unknown[] = [],
      filterConditions: unknown = {},
      groupConditions: unknown[] = [],
    ) => {
      let queryResponse: QueryRule = {};
      if (query) {
        queryResponse = await getQueryResponse(query);
        filterRef.current = { value: { id: query } };
      } else {
        filterRef.current = { value: null };
      }
      updateAllParameterOfGrid(queryResponse, columns, group);
      reactGridRef.current?.applyConditions?.({ sortConditions, filterConditions, groupConditions });
    };

    useEffect(() => {
      if (viewsLoaded && gridReady) {
        // `viewsRef` holds the same map, already narrowed through `isViewMap` on every render.
        const v = selectedView ? viewsRef.current[selectedView] : undefined;
        if (v) {
          initializeView(
            v.query ?? "",
            v.columns ?? [],
            v.group ?? [],
            v.sortConditions || [],
            v.filterConditions || {},
            v.groupConditions || [],
          );
        }
      }
    }, [viewsLoaded, gridReady]);

    useEffect(
      () => () => {
        dispatch(resetDatabank({ fileVersionId, id }));
      },
      [fileVersionId, id],
    );

    const handleQueryFilterChange = async (args: { id: string; query: string } | null) => {
      if (args) {
        filterRef.current = { value: { id: args.id } };
        // The saved query is stored as a JSON string; only a rule object is a usable filter tree
        // (anything else means "no filter", which is what `applyFilter` already made of it).
        const parsedQuery: unknown = JSON.parse(args.query);
        const queryData: QueryRule = isRecord(parsedQuery) ? parsedQuery : null;
        const value = args.id;
        const columns = await getDistinctFields(queryData);
        updateAllParameterOfGrid(queryData, columns, viewsRef.current[selectedViewRef.current]?.group);
        dispatch(
          updateCurrentDatabankView({
            data: {
              query: value,
              columns,
            },
            fileVersionId,
            id,
          }),
        );
      } else {
        updateAllParameterOfGrid(
          {},
          viewsRef.current[selectedViewRef.current]?.columns || [],
          viewsRef.current[selectedViewRef.current]?.group,
        );
        filterRef.current = { value: null };
        dispatch(
          updateCurrentDatabankView({
            data: {
              query: "",
            },
            fileVersionId,
            id,
          }),
        );
      }
    };

    const getDistinctFields = async (query: QueryRule): Promise<string[]> => {
      const fields = new Set<string>();
      // A query-builder node: either a group (`condition` + `rules`) or a leaf (`field`). Both keys
      // are read off the open rule record, so each is checked for the shape it is used at instead
      // of being asserted into it.
      const extractFields = (q: QueryRule | undefined) => {
        if (!q || typeof q !== "object" || Object.keys(q).length === 0) {
          return;
        }
        if (Object.hasOwn(q, "condition")) {
          const rules = Array.isArray(q.rules) ? q.rules : [];
          for (const rule of rules) {
            extractFields(isRecord(rule) ? rule : null);
          }
        } else if (typeof q.field === "string") {
          fields.add(q.field);
        }
      };
      extractFields(query);
      return Array.from(fields);
    };

    const handleAddMeasurementClick = async (takeoffsChecked: ObjectIdString[]) => {
      const selectedRecords = getSelectedDataItems();
      const recordWithoutUnitAndQuantity = selectedRecords.find((record) => {
        const quantity = record["properties.Quantity"];
        const unit = record["properties.Unit"];
        if (!quantity || !unit) {
          return true;
        }
      });
      if (recordWithoutUnitAndQuantity) {
        dispatch(
          showToastNotification({
            content: t("Quantity and Unit should not be empty!"),
            type: TOAST_TYPE_ERROR,
          }),
        );
      } else {
        try {
          // The Add-to-takeoff control only renders when estimateId is set (toolbar gating).
          if (estimateId == null) return;
          // `estimateId` reaches this component through the viewer's untyped (`any`) prop chain
          // from the `estimateId` URL query param and can still arrive as a legacy number, so the
          // String() coercion is VALIDATED rather than asserted into the branded id: the request
          // schema declares `estimateId: objectIdString`, so a malformed id is the same failure the
          // server would answer with — reported with the same toast, without the round trip.
          const estimateObjectId = objectIdString.safeParse(String(estimateId)).data;
          if (estimateObjectId == null) {
            dispatch(showToastNotification({ content: SOMETHING_WENT_WRONG, type: TOAST_TYPE_ERROR }));
            return;
          }
          const response = await addMeasurementsToTakeoffsFromNodes({
            nodes: isAllRowsSelectedRef.current
              ? []
              : selectedRecords.map(({ nodeId, fileVersionId }) => ({
                  nodeId,
                  fileVersionId,
                })),
            filterQuerySet: viewsRef.current[selectedViewRef.current]?.query,
            fileVersionId,
            estimateId: estimateObjectId,
            takeoffId: takeoffsChecked,
          });
          if (response.data.status) {
            const setData = prepareEstimationFieldNeedToRefresh({
              HoopsModalStatus,
              measurements: true,
              takeoffs: takeoffsChecked,
              estimateFieldsNeedToRefresh,
            });
            dispatch(setEstimateFieldsNeedtoRefresh(setData));
            dispatch(
              showToastNotification({
                content: response.data.message
                  ? untranslated(response.data.message)
                  : t("Measurement attached to takeoff successfully."),
                type: TOAST_TYPE_SUCCESS,
              }),
            );
          }
        } catch (e: unknown) {
          dispatch(
            showToastNotification({
              content: SOMETHING_WENT_WRONG,
              type: TOAST_TYPE_ERROR,
            }),
          );
          console.log(e);
          Promise.reject(e);
        }
      }
    };

    const handleSelectCallback = (selectedRecords: DatasetRow[]) => {
      if (!getIsViewerDependencyLoaded({ viewerImpl, isModelStructureReady, isSettingsApplied, hwv: CubeGlobal.hwv })) {
        dispatch(dispatchModelLoadingToast());
        return;
      }
      const mapper = getFileversionIdAndNodeOffsetIdMap(fileVersionId);
      viewerImpl?.hwv?.selectionManager?.clear();
      const selectedNodes: Selection.SelectionItem[] = [];
      for (const nodeData of selectedRecords) {
        try {
          selectedNodes.push(Selection.SelectionItem.create(nodeData.nodeId + mapper[nodeData.fileVersionId]!));
        } catch (e: unknown) {
          console.log(e);
          Promise.reject(e);
        }
      }
      viewerImpl?.hwv?.selectionManager?.add(selectedNodes, false);
    };

    const onSelectedRowsChanged = (args: { detail: { args: { rows: number[] } } }) => {
      const grid = reactGridRef.current?.slickGrid;
      const selectedRecords = grid ? args.detail.args.rows.map((rowIdx) => grid.getDataItem(rowIdx)) : [];
      if (selectedRecords.length >= 1) {
        setDisableTakeoffButton(false);
      } else {
        setDisableTakeoffButton(true);
      }
    };

    const handleSelectAllClick = () => {
      handleSelectCallback(getSelectedDataItems());
    };

    const handleIsolateOnInit = async (allRecords?: DatasetRow[]) => {
      if (!getIsViewerDependencyLoaded({ viewerImpl, isModelStructureReady, isSettingsApplied, hwv: CubeGlobal.hwv })) {
        dispatch(dispatchModelLoadingToast());
        return;
      }
      const mapper = getFileversionIdAndNodeOffsetIdMap(fileVersionId);
      if (allRecords?.length) {
        handleSelectCallback(allRecords);
        await CubeGlobal.isolateNodes(allRecords.map((data) => data.nodeId + mapper[data.fileVersionId]!));
      }
    };

    const handleIsolateAllClick = async () => {
      if (!getIsViewerDependencyLoaded({ viewerImpl, isModelStructureReady, isSettingsApplied, hwv: CubeGlobal.hwv })) {
        dispatch(dispatchModelLoadingToast());
        return;
      }
      const selectedRecords = getSelectedDataItems();
      const mapper = getFileversionIdAndNodeOffsetIdMap(fileVersionId);
      if (selectedRecords.length) {
        handleSelectAllClick();
        await CubeGlobal.isolateNodes(selectedRecords.map((data) => data.nodeId + mapper[data.fileVersionId]!));
      }
    };

    const onColorChange = (color: { value: string }) => (nodeColor.current = color.value);

    const handleSetColorAll = async () => {
      if (!getIsViewerDependencyLoaded({ viewerImpl, isModelStructureReady, isSettingsApplied, hwv: CubeGlobal.hwv })) {
        dispatch(dispatchModelLoadingToast());
        return;
      }
      const selectedRecords = getSelectedDataItems();
      const mapper = getFileversionIdAndNodeOffsetIdMap(fileVersionId);
      for (const nodeData of selectedRecords) {
        try {
          await CubeGlobal.setColorFunc(nodeData.nodeId + mapper[nodeData.fileVersionId]!, nodeColor.current);
        } catch (e: unknown) {
          console.log(e);
          Promise.reject(e);
        }
      }
    };

    const buildViewsToolbarItems = (): ToolbarItem[] =>
      hideViews
        ? []
        : [
            toolbarItem({
              template: ViewsDropdownItem,
              props: { getCurrentColumnsState, id, fileVersionId, onViewChange },
              id: "views",
            }),
          ];

    const buildAddToTakeoffItems = (): ToolbarItem[] =>
      disableAddMeasurementToTakeoff || estimateId == null
        ? []
        : [
            toolbarItem({
              template: AddToTakeoffItem,
              props: { handleAddMeasurementClick, estimateId, addToTakeoffButtonRef, onTakeoffButtonCreated },
            }),
            createSeparatorItem(),
          ];

    const buildQueryFilterItems = (): ToolbarItem[] =>
      hideQueryFiltering
        ? []
        : [
            toolbarItem({
              template: QueryFilterItem,
              props: { query, filterRef, handleQueryFilterChange, t },
            }),
            createSeparatorItem(),
          ];

    const buildQuantificationItems = (columns: string[]): ToolbarItem[] =>
      isFromQuery
        ? []
        : [
            toolbarItem({
              id: "quantification",
              template: QuantificationItem,
              props: {
                columns,
                t,
                portalMeasurementsUnit,
                handleGlobalQuantityChange,
                handleGlobalUnitChange,
                handleGlobalSettingsUpdate,
              },
            }),
            createSeparatorItem(),
          ];

    const initialiseToolbar = (columns: string[]) => {
      const items: ToolbarItem[] = [
        ...buildViewsToolbarItems(),
        createSeparatorItem(),
        ...(!estimateId || estimateError ? [] : buildAddToTakeoffItems()),
        ...buildQueryFilterItems(),
        toolbarItem({
          template: ActionButtons,
          props: {
            onSelectAll: handleSelectAllClick,
            onIsolateAll: handleIsolateAllClick,
            onSetColorAll: handleSetColorAll,
            colorValue: nodeColor.current,
            onColorChange,
          },
        }),
        createSeparatorItem(),
        ...(!estimateId || estimateError ? [] : buildQuantificationItems(columns)),
        toolbarItem({
          template: ColumnChooserBtn,
          props: { onClick: handleOpenColumnChooserModal },
          id: "columnChoose",
          rightAllign: true,
          sticky: true,
        }),
      ];
      setToolbar(items);
    };

    const applyLocalQuantityUnitUpdates = (
      nextData: ModelPropertiesData,
      records: DatasetRow[],
    ): ModelPropertiesData => {
      for (const row of records) {
        const { fileVersionId, nodeId } = row;
        const fileMap: Record<number, ModelPropertyNode> = nextData[fileVersionId]
          ? { ...nextData[fileVersionId] }
          : {};
        const existingNode = fileMap[nodeId];
        // Same in-flight-refetch window `handleGlobalSettingsUpdate` guards for: with no node in
        // the ref there is nothing to patch, and a synthesized identity-less node would be flattened
        // into an "undefined_undefined" grid row.
        if (!existingNode) continue;
        nextData[fileVersionId] = fileMap;
        const currNode: ModelPropertyNode = { ...existingNode };
        fileMap[nodeId] = currNode;
        const props: Record<string, ModelPropertyValue> = currNode.properties ? { ...currNode.properties } : {};
        if (globalUnitValue.current) {
          props.Unit = globalUnitValue.current || "";
        }
        if (globalQuantityValue.current) {
          const qty: number | string =
            Number.parseFloat(_.get(currNode, globalQuantityValue.current)) ||
            // The dotted-key read off a grid row is `unknown`; `parseFloat` stringifies whatever it
            // is given anyway, so the conversion is spelled out instead of asserted away.
            Number.parseFloat(String(_.get(row, globalQuantityValue.current))) ||
            "";
          props.Quantity = qty;
        }
        currNode.properties = props;
      }
      return nextData;
    };

    const handleGlobalSettingsUpdate = async () => {
      const selectedRecords = getSelectedDataItems();

      if (!selectedRecords.length) {
        dispatch(
          showToastNotification({
            content: t("No row selected"),
            type: TOAST_TYPE_ERROR,
          }),
        );
        return;
      }

      try {
        // The /queries/updateBulkQuantityAndUnitOfFile request body sends an open `params`
        // array (not modelled by updateBulkQuantityAndUnitOfFileRequest, which is a looseObject).
        const updateParams: Record<string, unknown>[] = [];
        const quantityUpdates: Array<{
          fileVersionId: string;
          nodeId: number;
          unit: string;
          quantity: string | number;
        }> = [];

        selectedRecords.forEach((data) => {
          const nodeMetadata = modelDataRef.current[data.fileVersionId]?.[data.nodeId];
          // Guard the in-flight refetch window: modelDataRef may be {} (released
          // before a concurrent refetch) while the old rows are still selectable
          // in the grid. Skip rows we can't resolve rather than dereferencing
          // undefined (parentMap/childrenMap below).
          if (!nodeMetadata) return;

          updateParams.push({
            fileVersionId: data.fileVersionId,
            fileId: data.fileId,
            projectId: data.projectId,
            quantity: _.get(data, globalQuantityValue.current) || "",
            unit: globalUnitValue.current || "",
            estimateId,
            parentMap: nodeMetadata.parentMap,
            childrenMap: nodeMetadata.childrenMap,
            parent: nodeMetadata.parent,
            portalId: nodeMetadata.portalId,
            nodeId: data.nodeId,
          });
          quantityUpdates.push({
            fileVersionId: data.fileVersionId,
            nodeId: data.nodeId,
            unit: globalUnitValue.current,
            quantity: _.get(modelDataRef.current[data.fileVersionId]?.[data.nodeId], globalQuantityValue.current) || "",
          });
        });
        // Predict the version this edit's bump will produce (current + 1) from
        // the latest committed modelProperties, and skip the refetch for only
        // that version (the edit is applied to the grid + ref locally below).
        // A foreign bump interleaving in the dispatch window is a rare exception
        // that would consume this prediction — acceptable, since a quantity edit
        // and a cross-panel edit are seldom simultaneous, and a missed skip just
        // costs a harmless refetch.
        skipRefetchForVersionRef.current = modelPropertiesStateRef.current.dataVersion + 1;
        // Single dispatch for the whole batch — `dataVersion` bumps once, not per record.
        dispatch(updateQuantityAndUnit(quantityUpdates));

        await axiosWrapper.post<UpdateBulkQuantityAndUnitOfFileResponse>(
          `${getBackendUrl()}/queries/updateBulkQuantityAndUnitOfFile`,
          { params: updateParams },
        );

        const nextData = applyLocalQuantityUnitUpdates({ ...modelDataRef.current }, selectedRecords);
        // Write the optimistic edit back into the ref (we skipped the refetch),
        // so later node lookups and the on-demand filter list stay consistent.
        modelDataRef.current = nextData;
        refreshDatasetFromModelData(nextData);

        dispatch(
          showToastNotification({
            content: t("Data updated successfully"),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
      } catch (e) {
        // The local patch may not have run; un-skip so the bump's refetch (if it
        // hasn't already fired) reconciles the grid with the persisted IDB state.
        skipRefetchForVersionRef.current = -1;
        console.log(e);
        dispatch(
          showToastNotification({
            content: SOMETHING_WENT_WRONG,
            type: TOAST_TYPE_ERROR,
          }),
        );
        Promise.reject(e);
      }
    };

    const handleSaveColumns = (data: string[]) => {
      const gridInstance = reactGridRef.current;
      if (gridInstance?.slickGrid) {
        // The grid's own checkbox-selector column is kept in front of the rebuilt set.
        const [selectorColumn] = gridInstance.slickGrid.getColumns();
        gridInstance.slickGrid.setColumns([
          ...(selectorColumn ? [selectorColumn] : []),
          ...asSlickColumns(getGridColumns(data)),
        ]);
        gridInstance.resizerService.resizeColumnsByCellContent(true);
      }
      if (!hideViews) {
        dispatch(
          updateCurrentDatabankView({
            data: {
              columns: data,
            },
            fileVersionId,
            id,
          }),
        );
      }
      handleOpenColumnChooserModal();
      if (onSaveColumns) {
        onSaveColumns(data);
      }
    };

    const handleOpenColumnChooserModal = () => {
      setOpenColumnChooserModal(!openColumnChooserModal);
    };

    const handleGlobalQuantityChange = (args: { field: string }) => {
      globalQuantityValue.current = args.field;
    };

    const handleGlobalUnitChange = (args: { value: string }) => {
      globalUnitValue.current = args.value;
    };

    const getCurrentColumnsState = () =>
      getFilteredColumns((reactGridRef.current?.slickGrid.getColumns() ?? []).slice(1).map((col) => col.field));

    const onColumnsReordered = () => {
      const columns = (reactGridRef.current?.slickGrid.getColumns() ?? [])
        .filter(
          (column, index) =>
            index != 0 &&
            !excludedColumnsConstant.some((data) => data.field == column.field) &&
            !compulsoryColumnsConstant.some((data) => data.field == column.field),
        )
        .map((column) => column.field);
      if (onSaveColumns) {
        onSaveColumns(columns);
      }
      if (!isFromQuery) {
        dispatch(
          updateCurrentDatabankView({
            data: {
              columns,
            },
            fileVersionId,
            id,
          }),
        );
      }
    };

    const handleConditionsChange = ({
      sortConditions,
      filterConditions,
      groupConditions,
    }: {
      sortConditions: NonNullable<DatabankViewData["sortConditions"]>;
      filterConditions: NonNullable<DatabankViewData["filterConditions"]>;
      groupConditions: NonNullable<DatabankViewData["groupConditions"]>;
    }) => {
      if (!hideViews) {
        dispatch(
          updateCurrentDatabankView({
            data: { sortConditions, filterConditions, groupConditions },
            fileVersionId,
            id,
          }),
        );
      }
    };

    // Called by SlickGrid1 when a column is hidden/shown via the column header dropdown
    const handleGridSaveColumns = () => {
      const columns = reactGridRef.current?.slickGrid
        .getVisibleColumns()
        .filter(
          (col, idx) =>
            idx !== 0 &&
            !excludedColumnsConstant.some((e) => e.field === col.field) &&
            !compulsoryColumnsConstant.some((e) => e.field === col.field),
        )
        .map((col) => col.field);
      if (columns && !hideViews) {
        dispatch(updateCurrentDatabankView({ data: { columns }, fileVersionId, id }));
      }
    };

    if (isModelDataWiped) return <ModelDataClearedState />;

    return (
      <div className={` ${isFromQuery ? "h-[25rem]" : "h-full"} flex flex-col`}>
        {openColumnChooserModal && (
          <Suspense>
            <ColumnChooser
              handleOpenColumnChooserModal={handleOpenColumnChooserModal}
              handleSaveColumns={handleSaveColumns}
              data={getFilteredColumns(
                (columnDefinitions ?? []).slice(1).map((data) => ({
                  field: data.field,
                  label: data.label,
                })),
              )}
              selectedData={getCurrentColumnsState()}
            />
          </Suspense>
        )}
        {gridOptions && columnDefinitions && (
          <div className="h-full w-full flex flex-col">
            <div id={id} className="h-full w-full overflow-hidden">
              <SlickGrid1
                gridId={`${id}_grid18`}
                showLoader={showShimmer}
                toolbar={toolbar}
                ref={reactGridRef}
                toolbarHeight={16}
                enableWrapText={enableWrapText}
                dataset={dataset}
                setEnableWrapText={setEnableWrapText}
                columnDefinitions={columnDefinitions}
                gridOptions={gridOptions}
                showPageCount={false}
                onColumnsReordered={onColumnsReordered}
                excelExport={true}
                enableGroupingOption={allowGrouping}
                enableRowResize={true}
                enableFilteringOption
                hasCustomColumnChooser
                enableSortingOption
                onReactGridCreated={($event) => reactGridReady($event)}
                onSelectedRowsChanged={onSelectedRowsChanged}
                onConditionsChange={handleConditionsChange}
                onSaveColumns={handleGridSaveColumns}
              />
            </div>
          </div>
        )}
      </div>
    );
  },
);

SearchSet.displayName = "SearchSet";
export default SearchSet;
